import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import {
  ClientDetailsInterceptor,
  SatHeaderInterceptor,
  initializeKeycloak,
  provideMixpanelAnalytics,
  UiModule,
} from '@irembo-andela/irembogov3-common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { environment } from '../environments/environment';
import { TranslationInitService } from './core/services/translation-init.service';

export function initializeTranslations(
  translationInitService: TranslationInitService
) {
  return () => {
    return translationInitService.initializeTranslations();
  };
}
import { IdleTimerService } from './core/services/idle-timer/idle-timer.service';
import { NgIdleModule } from '@ng-idle/core';
import { WelcomeComponent } from './welcome.component';
import { AddHeaderInterceptor } from './core/interceptors/add-header-interceptor.service';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { SharedModule } from './modules/shared/shared.module';
import { LocaleService } from './core/services/locale/locale.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, finalize, of } from 'rxjs';

export function HttpLoaderFactory(
  http: HttpClient,
  localeService: LocaleService
) {
  return new CustomLoader(http, localeService);
}
export class CustomLoader implements TranslateLoader {
  constructor(
    private httpClient: HttpClient,
    private localeService: LocaleService
  ) {}
  getTranslation(lang: string): Observable<any> {
    const header = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const apiAddress = `${environment.translationSourceUrl}/admin/v1/translations/static-text/by-locale/${lang}`;
    if (!this.localeService.translationsLoadedBefore) {
      this.localeService.loadingTranslations.next(true);
    }
    return this.httpClient.get(apiAddress, { headers: header }).pipe(
      finalize(() => {
        this.localeService.loadingTranslations.next(false);
        this.localeService.translationsLoadedBefore = true;
      }),
      catchError(() => {
        // Return an empty object to trigger missing translation handler
        return of({});
      })
    );
  }
}
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  TranslateLoader,
  TranslateModule,
  TranslateStore,
} from '@ngx-translate/core';
import { LayoutModule } from './layout/layout.module';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RouterModule } from '@angular/router';
import { DashboardComponent } from './modules/dashboard/dashboard/dashboard.component';

@NgModule({
  declarations: [AppComponent, WelcomeComponent, DashboardComponent],
  imports: [
    BrowserModule,
    CommonModule,
    UiModule,
    AppRoutingModule,
    KeycloakAngularModule,
    NgIdleModule.forRoot(),
    HttpClientModule,
    NgIdleKeepaliveModule.forRoot(),
    SharedModule.forRoot(),
    FormsModule,
    ReactiveFormsModule,
    LayoutModule,
    NgbDropdownModule,
    RouterModule,
    TranslateModule.forRoot({
      defaultLanguage:
        localStorage.getItem('locale') ?? environment.DEFAULT_LOCALE,
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient, LocaleService],
      },
      isolate: false,
    }),
  ],
  providers: [
    NgbModal,
    TranslateStore,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTranslations,
      deps: [TranslationInitService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (keycloakService: KeycloakService) => {
        return initializeKeycloak(keycloakService, {
          url: environment.authServiceUrl,
          realm: environment.authRealm,
          clientId: environment.authClientId,
          redirectUri: environment.authSuccessRedirectUrl,
        });
      },
      multi: true,
      deps: [KeycloakService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ClientDetailsInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SatHeaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AddHeaderInterceptor,
      multi: true,
    },
    IdleTimerService,
    {
      provide: 'environment',
      useValue: environment,
    },
    ...provideMixpanelAnalytics({
      useFactory: (locale: LocaleService) => ({
        isActive: environment.IS_SENDING_ANALYTICS_EVENTS_ACTIVE,
        token: environment.MIXPANEL_TOKEN,
        getLocale: () => locale.activeLocaleSubject.value?.locale,
      }),
      deps: [LocaleService],
    }),
  ],
  bootstrap: [AppComponent],
  exports: [TranslateModule],
})
export class AppModule {}
